import { Component, ReactNode, createRef } from 'react'
import * as styles from 'components/features/pfm/SidebarGroups/SidebarGroups.styl'
import * as cn from 'classnames'
import Touchable from 'components/utils/Touchable'
import * as debounce from 'lodash/debounce'
import { SHOW_SIDEBAR_POPUP_MOUSE_TIMEOUT_SHORT, SHOW_SIDEBAR_POPUP_MOUSE_TIMEOUT_LONG, HIDE_SIDEBAR_POPUP_MOUSE_LEFT_TIMEOUT } from 'constants/component'
import withMobile, { WithMobileProps } from 'hocs/withMobile/withMobile'

export type SidebarGroupsItemColor =
  | 'oxford'
  | 'coral'
  | 'merlot'
  | 'smoke'

export type SidebarGroupsItemProps =
  & WithMobileProps
  & {
    color: SidebarGroupsItemColor
    selected?: boolean
    // if not selectable - fill all the circle
    selectable?: boolean

    icon?: ReactNode

    onSelect?: () => void

    isAnyPopupShown: boolean
    isPopupShown: boolean
    blockPopup?: boolean
    onShowPopup?: (rect: ClientRect) => void
    onHidePopup?: () => void

    hideTimeoutId: number | null
    onRegisterHideTimeout: (timeoutId: number) => void
  }

class SidebarGroupsItemComponent extends Component<SidebarGroupsItemProps> {
  private containerRef = createRef<HTMLDivElement>()

  render () {
    const {
      selected, selectable, onSelect,
      color, icon,
      isMobileOrTablet,
      children,
      blockPopup,
    } = this.props

    const itemClassname = cn(styles.baseItem, styles.item, styles[color], {
      [styles.selected]: selected,
      [styles.selectable]: selectable,
    })

    const handleTextTap = blockPopup ? onSelect : this.togglePopup
    const handleTextClick = isMobileOrTablet
      ? blockPopup ? onSelect : this.togglePopup
      : undefined

    return (
      <div
        className={ itemClassname }
        onMouseEnter={ isMobileOrTablet ? undefined : this.handleMouseEnter }
        onMouseLeave={ isMobileOrTablet ? undefined : this.handleMouseLeave }
        onClick={ isMobileOrTablet ? undefined : onSelect }
        ref={ this.containerRef }
      >
        <Touchable
          onTap={ onSelect }
          onClick={ isMobileOrTablet ? onSelect : undefined }
        >
          <div className={ styles.checkmark } />
        </Touchable>
        <Touchable
          onTap={ handleTextTap }
          onClick={ handleTextClick }
        >
          <div className={ styles.itemTextWrapper }>
            <span className={ styles.itemText }>{ children }</span>
            { icon && (
              <div className={ styles.itemIcon }>{ icon }</div>
            )}
          </div>
        </Touchable>
      </div>
    )
  }

  componentWillUnmount () {
    this.showPopupDelayedShort.cancel()
    this.showPopupDelayedLong.cancel()
    this.clearTimeoutOnMouseEnter.cancel()
  }

  private togglePopup = () => {
    const { isPopupShown, onHidePopup } = this.props
    if (isPopupShown) {
      if (onHidePopup) {
        onHidePopup()
      }
    } else {
      this.showPopup()
    }
  }

  private showPopup = () => {
    const { onShowPopup, isPopupShown } = this.props
    if (onShowPopup && !isPopupShown && this.containerRef.current) {
      this.clearTimeout()
      onShowPopup(this.containerRef.current.getBoundingClientRect())
    }
  }

  private showPopupDelayedShort = debounce(this.showPopup, SHOW_SIDEBAR_POPUP_MOUSE_TIMEOUT_SHORT)
  private showPopupDelayedLong = debounce(this.showPopup, SHOW_SIDEBAR_POPUP_MOUSE_TIMEOUT_LONG)


  private clearTimeout () {
    const { hideTimeoutId } = this.props
    if (typeof hideTimeoutId === 'number') {
      clearTimeout(hideTimeoutId)
    }
  }

  // debounce because sometimes mouse enter is called before mouse leave
  private clearTimeoutOnMouseEnter = debounce(() => {
    const { isPopupShown } = this.props
    if (isPopupShown) {
      this.clearTimeout()
    }
  }, 20)

  private handleMouseEnter = () => {
    const { isAnyPopupShown } = this.props
    if (isAnyPopupShown) {
      this.showPopupDelayedLong()
    } else {
      this.showPopupDelayedShort()
    }

    this.clearTimeoutOnMouseEnter()
  }

  private handleMouseLeave = () => {
    const { onRegisterHideTimeout, onHidePopup, isPopupShown } = this.props
    this.showPopupDelayedShort.cancel()
    this.showPopupDelayedLong.cancel()

    if (isPopupShown && onHidePopup) {
      const timeoutId = window.setTimeout(onHidePopup, HIDE_SIDEBAR_POPUP_MOUSE_LEFT_TIMEOUT)
      onRegisterHideTimeout(timeoutId)
    }
  }
}

export const SidebarGroupsItem = withMobile(SidebarGroupsItemComponent)
